import { makeObservable, observable, action, computed } from 'mobx';
import { BaseService, EventSubscription } from './base/BaseService';
import { ISpeakerService, SpeakerAssignmentResult, SpeakerEvent, SpeakerRepository, DatoSpeaker } from './interfaces/ISpeakerService';
import { TranscriptSpeaker } from '../models/TranscriptSpeaker';
import { TranscriptVoiceMapping } from '../types';
import { time } from 'console';

/**
 * Speaker color palette for automatic assignment
 */
const DEFAULT_SPEAKER_COLORS = [
  '#4299E1', // Blue
  '#48BB78', // Green
  '#ED8936', // Orange
  '#9F7AEA', // Purple
  '#F56565', // Red
  '#38B2AC', // Teal
  '#ED64A6', // Pink
  '#ECC94B', // Yellow
  '#667EEA', // Indigo
  '#FC8181', // Light Red
  '#4FD1C5', // Light Teal
  '#B794F4', // Light Purple
];

/**
 * Service for managing speakers and speaker assignments
 */
export class SpeakerService extends BaseService<SpeakerEvent> implements ISpeakerService {
  // Speakers in the current transcript
  private _speakers: Map<string, TranscriptSpeaker> = new Map();

  // Speaker mappings between transcript speakers and DatoCMS speakers
  private _speakerMappings: TranscriptVoiceMapping | null = null;

  // Story ID and Album ID
  private _storyId: string | null = null;
  private _albumId: string | null = null;

  // Speaker repository (for DatoCMS operations)
  private _speakerRepository: SpeakerRepository | null = null;

  // Loading state
  private _isLoading: boolean = false;

  // Speaker color cache for consistent colors
  private _speakerColorCache = new Map<string, string>();

  // Memoized cache for ensureSpeakerReferencesWithRetry
  private _ensureSpeakerReferencesCache: Map<string, Promise<boolean>> = new Map();

  // A lock system for transactional updates
  private static _locks = new Map<string, boolean>();

  /**
   * Creates a new SpeakerService
   */
  constructor() {
    super(false); // Do not use makeAutoObservable in BaseService
    
    // Use makeObservable with no configuration for now
    makeObservable(this);
  }

  /**
   * Initializes the speaker service with repository and IDs
   *
   * @param speakerRepository Repository for Dato CMS operations
   * @param storyId Story ID
   * @param albumId Album ID
   */
  initialize(
    speakerRepository: SpeakerRepository,
    storyId: string | null,
    albumId: string | null,
  ): void {
    this._speakerRepository = speakerRepository;
    this._storyId = storyId;
    this._albumId = albumId;

    if (storyId) {
      this.loadSpeakers(storyId);
    }
  }

  /**
   * Loads speakers for a story
   *
   * @param storyId Story ID
   */
  async loadSpeakers(storyId: string): Promise<void> {
    if (!this._speakerRepository) {
      throw new Error('Speaker repository not initialized');
    }

    this._isLoading = true;
    this.notify(SpeakerEvent.LOADING_CHANGED, { loading: true });

    try {
      // Load speakers from DatoCMS
      const { albumId, storySpeakers, albumSpeakers } =
        await this._speakerRepository.getStorySpeakers(storyId);

      // Set album ID
      this._albumId = albumId;

      // Convert DatoCMS speakers to TranscriptSpeaker objects
      this._speakers.clear();

      // Add story-specific speakers first
      for (const speaker of storySpeakers) {
        this.addDatoSpeakerToLocalCache(speaker);
      }

      // Add album speakers that aren't already added
      for (const speaker of albumSpeakers) {
        if (!this._speakers.has(speaker.id)) {
          this.addDatoSpeakerToLocalCache(speaker);
        }
      }
  

      // Notify listeners
      this.notify(SpeakerEvent.SPEAKERS_LOADED, {
        speakers: this.getSpeakers(),
      });
    } catch (error) {
      console.error('Error loading speakers:', error);
      this.notify(SpeakerEvent.ERROR, {
        error: 'Failed to load speakers',
      });
    } finally {
      this._isLoading = false;
      this.notify(SpeakerEvent.LOADING_CHANGED, { loading: false });
    }
  }

  /**
   * Adds a Dato CMS speaker to the local speaker cache
   *
   * @param datoSpeaker Dato CMS speaker
   */
  private addDatoSpeakerToLocalCache(datoSpeaker: DatoSpeaker): void {
    const color = this.getNextSpeakerColor();

    // Create a new TranscriptSpeaker with the anonymous ID and Dato mapping
    const speaker = new TranscriptSpeaker(
      '',      // Use generated anonymous ID as the speaker label
      datoSpeaker.id, // Store Dato ID as reference for mapping
      datoSpeaker.name,
      color,
      datoSpeaker.title,
      datoSpeaker.voice,
      datoSpeaker.bio
    );


    // store a direct reference using the datoSpeakerId as key
    // This helps with lookups when trying to find speakers by their Dato ID
    this._speakers.set(datoSpeaker.id, speaker);

  }

  /**
   * Gets the next speaker color from the palette
   *
   * @returns A color code
   */
  private getNextSpeakerColor(): string {
    const currentCount = this._speakers.size;
    return DEFAULT_SPEAKER_COLORS[currentCount % DEFAULT_SPEAKER_COLORS.length] || '#A9A9A9';
  }

  /**
   * Loads speaker mappings from a mapping object
   *
   * @param mappings Speaker mappings
   */
  loadSpeakerMappings(mappings: TranscriptVoiceMapping): void {
    this._speakerMappings = mappings;

    // When mappings are loaded, ensure any Dato speakers are properly referenced
    this.ensureAllSpeakerReferences();
    
    // Check that we have samples of each type of speaker
    const anonymousSpeakers = Array.from(this._speakers.keys()).filter(k => k.startsWith('spk_'));
    const datoSpeakers = Array.from(this._speakers.keys()).filter(k => !k.startsWith('spk_'));
    
    console.log(`- Anonymous speakers: ${anonymousSpeakers.length}`);
    console.log(`- Dato speakers: ${datoSpeakers.length}`);

    // Notify listeners
    this.notify(SpeakerEvent.MAPPINGS_UPDATED, {
      mappings: this._speakerMappings,
    });
  }


  /**
   * Ensures all Dato speakers in the current mappings are properly referenced
   * in both the story and album
   */
  private ensureAllSpeakerReferences(): void {
    if (!this._speakerMappings || !this._speakerMappings.mappings || !this._speakerRepository) {
      return;
    }
    // console.log('Ensuring all Dato speaker references', this._speakerMappings);
    // Get all unique Dato speaker IDs from the mappings
    const datoSpeakerIds = new Set<string>();
    for (const [_, datoSpeakerId] of Object.entries(
      this._speakerMappings.mappings,
    )) {
      if (
        datoSpeakerId &&
        typeof datoSpeakerId === 'string' &&
        !datoSpeakerId.startsWith('spk_')
      ) {
        datoSpeakerIds.add(datoSpeakerId);
      }
    }

    // Ensure each Dato speaker is referenced properly
    for (const datoSpeakerId of datoSpeakerIds) {
      this.ensureSpeakerReferencesAsync(datoSpeakerId);
    }
  }

  /**
   * Gets a speaker color by its Dato CMS ID
   * @param speakerLabel The anonymous label of the speaker
   * @returns The speaker color or '#A9A9A9' if not found
   */
  getSpeakerColor(speakerLabel: string): string {
    if (!speakerLabel) return '#A9A9A9';

    if (this._speakerColorCache.has(speakerLabel)) {
      return this._speakerColorCache.get(speakerLabel) || '#A9A9A9';
    }
    
    // Generate consistent color
    const colors = [
      '#F2D093', // Gold
      '#6C9BA5', // Blue
      '#A46C85', // Purple
      '#B56E4F', // Brown
      '#91A46C', // Green
      '#6C6EA4', // Indigo
      '#A4A46C', // Olive
      '#A46C6C', // Red
      '#6CA4A4', // Teal
      '#A46CA4', // Pink
      '#6CA46C', // Lime
      '#A46C91', // Salmon
      '#916CA4', // Lavender
      '#6CA491', // Mint
      '#A4916C', // Beige
      '#A49191', // Gray
      '#9191A4', // Navy
      '#6C91A4', // Cyan
      '#A4A491', // Tan
    ];
    
    // Hash the speaker ID using the last 4 characters
    const hash = speakerLabel.slice(-4).split('').reduce((a: number, b: string) => {
      return a + b.charCodeAt(0);
    }, 0);
    
    const color = colors[hash % colors.length] || '#A9A9A9';
    this._speakerColorCache.set(speakerLabel, color);
    return color;
  };

  /**
   * Gets a speaker title by its anonymous label
   * @param speakerLabel The anonymous label of the speaker
   * @returns The speaker title or undefined if not found
   */
  getSpeakerTitle(speakerLabel: string): string | undefined {
    if (!speakerLabel) return undefined;
    
    // Next check if it's mapped to a Dato speaker
    const datoSpeakerId = this._speakerMappings?.mappings[speakerLabel];
    if (!datoSpeakerId) {
      // It's an unmapped anonymous speaker
      return '';
    }

    // Look for the speaker by datoSpeakerId in our speakers collection
    for (const speaker of this._speakers.values()) {
      if (speaker.datoSpeakerId === datoSpeakerId) {
        return speaker.title;
      }
    }
  }   

  getSpeakerVoice(speakerLabel: string): string | undefined {
    if (!speakerLabel) return undefined;
    
    // Next check if it's mapped to a Dato speaker
    const datoSpeakerId = this._speakerMappings?.mappings[speakerLabel];
    if (!datoSpeakerId) { 
      // It's an unmapped anonymous speaker
      return '';
    }

    // Look for the speaker by datoSpeakerId in our speakers collection
    for (const speaker of this._speakers.values()) {
      if (speaker.datoSpeakerId === datoSpeakerId) {
        return speaker.voice;
      }
    }
  }

  getSpeakerBio(speakerLabel: string): string | undefined {
    if (!speakerLabel) return undefined;
    
    // Next check if it's mapped to a Dato speaker
    const datoSpeakerId = this._speakerMappings?.mappings[speakerLabel];
    if (!datoSpeakerId) {
      // It's an unmapped anonymous speaker
      return '';
    }
    
    for (const speaker of this._speakers.values()) {
      if (speaker.datoSpeakerId === datoSpeakerId) {
        return speaker.bio;
      }
    }
  }

  /**
   * Gets a speaker name by its anonymous label
   * @param speakerLabel The anonymous label of the speaker
   * @returns The speaker name or undefined if not found
   */
  getSpeakerName(speakerLabel: string): string | undefined {
    if (!speakerLabel) return 'Unknown';
    
    // Next check if it's mapped to a Dato speaker
    const datoSpeakerId = this._speakerMappings?.mappings[speakerLabel];
    if (!datoSpeakerId) {
      // It's an unmapped anonymous speaker
      return `Speaker ${parseInt(speakerLabel.replace('spk_', '')) + 1}`;
    }
    
    // Look for the speaker by datoSpeakerId in our speakers collection
    for (const speaker of this._speakers.values()) {
      if (speaker.datoSpeakerId === datoSpeakerId) {
        return speaker.name;
      }
    }
    
    // Fallback to a basic formatted name if no speaker is found
    return `Speaker ${speakerLabel.replace('spk_', '')}`;
  }

  /**
   * Creates a new speaker
   *
   * @param name Speaker name
   * @param title Optional speaker title
   * @param bio Optional speaker bio
   * @param voice Optional speaker voice
   * @param skipStoryAddition Optional flag to skip adding the speaker to the story
   * @param options Optional location options (where to add the speaker)
   * @returns The new speaker or null if creation failed
   */
  async createSpeaker(
    name: string,
    title?: string,
    bio?: string,
    voice?: string,
    skipStoryAddition?: boolean,
  ): Promise<DatoSpeaker | null> {
    if (!this._speakerRepository) {
      throw new Error('Speaker repository not initialized');
    }

    // Create speaker in DatoCMS
    const datoSpeaker = await this._speakerRepository.createSpeaker(
      name,
      title,
      bio,
      voice,
    );

    if (!datoSpeaker) {
      this.notify(SpeakerEvent.ERROR, {
        error: 'Failed to create speaker',
      });
      return null;
    }

    // Add to story if specified and ID is available
    if (!skipStoryAddition && this._storyId) {
      await this._speakerRepository.addSpeakerReferenceToStory(
        datoSpeaker.id,
        this._storyId,
      );
    }

    // Add to album/organization if specified and ID is available
    if (this._albumId) {
      await this._speakerRepository.addSpeakerReferenceToAlbum(
        datoSpeaker.id,
        this._albumId,
      );
    }
    
    // Add to local cache
    const color = this.getNextSpeakerColor();
    const speaker = new TranscriptSpeaker(
      '',      // Use generated anonymous ID as the speaker label
      datoSpeaker.id, // Store Dato ID as reference
      datoSpeaker.name,
      color,
    );

    this._speakers.set(datoSpeaker.id, speaker);    

    // Notify listeners
    this.notify(SpeakerEvent.SPEAKER_ADDED, {
      speaker,
    });

    return datoSpeaker;
  }

  /**
   * Updates a speaker's local properties (color)
   *
   * @param speakerLabel Speaker label
   * @param updates Updates to apply to the speaker locally
   * @returns True if update was successful
   */
  updateSpeaker(
    speakerLabel: string,
    updates: { name?: string; color?: string },
  ): boolean {
    const speaker = this._speakers.get(speakerLabel);

    if (!speaker) {
      return false;
    }

    // Apply updates
    if (updates.name !== undefined) {
      speaker.name = updates.name;
    }

    if (updates.color !== undefined) {
      speaker.color = updates.color;
    }

    // Update in cache
    this._speakers.set(speakerLabel, speaker);

    // Notify listeners
    this.notify(SpeakerEvent.SPEAKER_UPDATED, {
      speaker,
    });

    return true;
  }

  /**
   * Updates a speaker's properties in DatoCMS
   *
   * @param datoSpeakerId The DatoCMS speaker ID to update
   * @param updates The properties to update (name, title, bio, voice)
   * @returns Promise resolving to true if update was successful
   */
  async updateSpeakerProperties(
    datoSpeakerId: string,
    updates: {
      name?: string;
      title?: string;
      bio?: string;
      voice?: string;
    }
  ): Promise<boolean> {
    if (!this._speakerRepository) {
      console.error('Cannot update speaker properties: Speaker repository not initialized');
      return false;
    }

    try {
      // Update the speaker in DatoCMS
      const updatedSpeaker = await this._speakerRepository.updateSpeaker(
        datoSpeakerId,
        updates
      );

      if (!updatedSpeaker) {
        this.notify(SpeakerEvent.ERROR, {
          error: 'Failed to update speaker properties in DatoCMS',
        });
        return false;
      }

      // Update the speaker in the local cache
      const speaker = this._speakers.get(datoSpeakerId);
      if (speaker) {
        if (updates.name !== undefined) {
          speaker.name = updates.name;
        }
        if (updates.title !== undefined) {
          speaker.title = updates.title;
        }
        if (updates.bio !== undefined) {
          speaker.bio = updates.bio;
        }
        if (updates.voice !== undefined) {
          speaker.voice = updates.voice;
        }

        // Store the updated speaker in the cache
        this._speakers.set(datoSpeakerId, speaker);

        // Notify listeners
        this.notify(SpeakerEvent.SPEAKER_UPDATED, {
          speaker,
        });
      } else {
        // If speaker wasn't in the local cache, add it
        const color = this.getNextSpeakerColor();
        const newSpeaker = new TranscriptSpeaker(
          '',
          datoSpeakerId,
          updatedSpeaker.name,
          color,
          updatedSpeaker.title,
          updatedSpeaker.voice,
          updatedSpeaker.bio
        );

        this._speakers.set(datoSpeakerId, newSpeaker);

        // Notify listeners
        this.notify(SpeakerEvent.SPEAKER_ADDED, {
          speaker: newSpeaker,
        });
      }

      return true;
    } catch (error) {
      console.error('Error updating speaker properties:', error);
      this.notify(SpeakerEvent.ERROR, {
        error: 'Failed to update speaker properties: ' + (error as Error).message,
      });
      return false;
    }
  }

  /**
   * Removes a speaker from the local cache
   *
   * @param speakerLabel Speaker label
   * @returns True if removal was successful
   */
  removeSpeaker(speakerLabel: string): boolean {
    const speaker = this._speakers.get(speakerLabel);

    if (!speaker) {
      return false;
    }

    // Remove from cache
    this._speakers.delete(speakerLabel);

    // Notify listeners
    this.notify(SpeakerEvent.SPEAKER_REMOVED, {
      speakerLabel,
    });

    return true;
  }

  /**
   * Acquires a lock for transactional operations
   * @param lockKey The key for the lock
   * @returns Promise resolving when lock is acquired
   */
  private async acquireLock(lockKey: string): Promise<void> {
    // More efficient implementation that avoids setTimeout polling
    return new Promise<void>((resolve) => {
      // Fast path: try to acquire the lock immediately
      if (!SpeakerService._locks.get(lockKey)) {
        SpeakerService._locks.set(lockKey, true);
        resolve();
        return;
      }
      
      // If we can't get the lock immediately, set up a faster retry mechanism
      // with exponential backoff to reduce CPU usage
      let attempts = 0;
      const maxAttempts = 10;
      
      const checkAndAcquire = () => {
        if (!SpeakerService._locks.get(lockKey)) {
          SpeakerService._locks.set(lockKey, true);
          resolve();
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          // Use exponential backoff with a cap at 100ms
          const delay = Math.min(Math.pow(1.5, attempts) * 10, 100);
          setTimeout(checkAndAcquire, delay);
        } else {
          // After max attempts, force acquire the lock
          console.warn(`Force acquiring lock after ${maxAttempts} attempts: ${lockKey}`);
          SpeakerService._locks.set(lockKey, true);
          resolve();
        }
      };
      
      setTimeout(checkAndAcquire, 10);
    });
  }

  /**
   * Releases a lock
   * @param lockKey The key for the lock
   */
  private releaseLock(lockKey: string): void {
    SpeakerService._locks.delete(lockKey);
  }

  /**
   * Loads the latest mappings from storage
   * @returns Promise resolving when mappings are loaded
   */
  private async loadLatestMappings(): Promise<void> {
    if (!this._storyId) {
      console.error('Cannot load latest mappings: No story ID');
      return;
    }

    try {
      const url = `${process.env.REACT_APP_API_URL}/api/story/${this._storyId}/transcript-data?resource=speaker-mappings&storyId=${this._storyId}`;
      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        if (data && data.data) {
          this._speakerMappings = data.data;
        }
      }
    } catch (error) {
      console.error('Error loading latest mappings:', error);
    }
  }

  /**
   * Updates mappings transactionally to prevent race conditions
   * @param updates Map of speaker ID to mapping value
   * @returns Promise resolving to true if update was successful
   */
  async updateMappingsTransactionally(
    updates: Record<string, string>,
  ): Promise<boolean> {
    if (!this._speakerMappings || !this._storyId) {
      console.error('Cannot update mappings: No mappings or story ID');
      return false;
    }

    const lockKey = `speaker_mapping_lock_${this._storyId}`;
    try {
      // Acquire lock
      await this.acquireLock(lockKey);

      // Reload latest mappings to ensure we're working with current data
      await this.loadLatestMappings();

      // Apply updates
      for (const [key, value] of Object.entries(updates)) {
        this._speakerMappings.mappings[key] = value;
      }

      // Save with updated timestamp
      this._speakerMappings.lastUpdated = Date.now();
      await this.saveSpeakerMappings();

      return true;
    } catch (error) {
      console.error('Failed to update mappings transactionally:', error);
      return false;
    } finally {
      // Release lock
      this.releaseLock(lockKey);
    }
  }

  /**
   * Gets all speakers
   *
   * @returns Array of speakers
   */
  getSpeakers(): TranscriptSpeaker[] {
    return Array.from(this._speakers.values());
  }

  getSpeakersCache(): Map<string, TranscriptSpeaker> {
    return this._speakers;
  }

  /**
   * Gets the current speaker mappings
   *
   * @returns Speaker mappings or null if not loaded
   */
  getSpeakerMappings(): TranscriptVoiceMapping | null {
    return this._speakerMappings;
  }
  
  /**
   * Directly update a single speaker mapping
   * 
   * @param speakerLabel The anonymous speaker label (e.g., "spk_0")
   * @param datoSpeakerId The Dato CMS speaker ID to map to
   * @returns Promise resolving to success status
   */
  async updateSpeakerMapping(speakerLabel: string, datoSpeakerId: string): Promise<boolean> {
    // Verify we have mappings
    if (!this._speakerMappings) {
      console.error('Cannot update speaker mapping: No mappings available');
      return false;
    }
    
    // Simple update - map the anonymous label to the Dato speaker ID
    return this.updateMappingsTransactionally({ [speakerLabel]: datoSpeakerId });
  }

  /**
   * Reassigns an anonymous speaker ID to another anonymous speaker ID
   * This is for fixing transcription errors where speaker boundaries are wrong
   *
   * @param fromSpeakerLabel The original anonymous speaker ID (e.g., "spk_0")
   * @param toSpeakerLabel The new anonymous speaker ID (e.g., "spk_1")
   * @param elementIds The specific element IDs to reassign
   * @returns Result of the operation
   */
  async reassignAnonymousSpeaker(
    fromSpeakerLabel: string,
    toSpeakerLabel: string,
    elementIds: string[],
  ): Promise<SpeakerAssignmentResult> {
    if (!this._speakerMappings) {
      return {
        success: false,
        speakerLabel: toSpeakerLabel,
        elementIds,
        error: 'Speaker mappings not loaded',
      };
    }

    if (!fromSpeakerLabel.startsWith('spk_') || !toSpeakerLabel.startsWith('spk_')) {
      return {
        success: false,
        speakerLabel: toSpeakerLabel,
        elementIds,
        error: 'Both speakers must be anonymous speakers (spk_X format)',
      };
    }

    try {
      console.log(
        `Reassigning anonymous speaker ${fromSpeakerLabel} to ${toSpeakerLabel} for ${elementIds.length} elements`,
      );

      // For each element, we need to change its speaker_label in the transcript
      // This is handled outside the service via a command that edits the transcript

      // Prepare transactional updates
      const updates: Record<string, string> = {};

      // The mapping is also updated to ensure any named speaker assignment follows
      // Store the original mapping of the 'fromSpeakerId'
      const originalMapping = this._speakerMappings.mappings[fromSpeakerLabel];

      // If there's an existing named speaker mapping for the 'fromSpeakerId',
      // we should transfer that to the 'toSpeakerId' for these specific elements
      if (originalMapping && !originalMapping.startsWith('spk_')) {
        // Add the mapping update for the target speaker
        updates[toSpeakerLabel] = originalMapping;
      }

      // Update mappings transactionally
      const updateSuccess = await this.updateMappingsTransactionally(updates);

      if (!updateSuccess) {
        return {
          success: false,
          speakerLabel: toSpeakerLabel,
          elementIds,
          error: 'Failed to update mappings transactionally',
        };
      }

      // Notify listeners
      this.notify(SpeakerEvent.SPEAKER_ASSIGNED, {
        speakerLabel: toSpeakerLabel,
        elementIds,
        mappings: this._speakerMappings,
      });

      return {
        success: true,
        speakerLabel: toSpeakerLabel,
        elementIds,
      };
    } catch (error) {
      console.error('Error reassigning anonymous speaker:', error);
      return {
        success: false,
        speakerLabel: toSpeakerLabel,
        elementIds,
        error: (error as Error).message,
      };
    }
  }

  notifyObservers(event: SpeakerEvent, data: any): void {
    this.notify(event, data);
  }

  /**
   * Save speaker mappings to transcriptVoices.json with retry mechanism
   *
   * @param maxRetries Maximum number of retry attempts
   * @returns Promise resolving to true if save was successful
   */
  async saveSpeakerMappings(maxRetries = 3): Promise<boolean> {
    if (!this._speakerMappings || !this._storyId) {
      console.error('Cannot save speaker mappings: No mappings or story ID');
      return false;
    }

    let retries = 0;

    while (retries < maxRetries) {
      try {
        const url = `${process.env.REACT_APP_API_URL}/api/story/${this._storyId}/transcript-data?resource=speaker-mappings&storyId=${this._storyId}`;

        // Update the timestamp to reflect the latest changes
        this._speakerMappings.lastUpdated = Date.now();

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            data: this._speakerMappings,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Failed to save speaker mappings: ${response.status} ${errorText}`,
          );
        }

        // Notify about successful update
        this.notify(SpeakerEvent.MAPPINGS_UPDATED, {
          mappings: this._speakerMappings,
        });

        return true;
      } catch (error) {
        retries++;
        console.warn(
          `Error saving speaker mappings (attempt ${retries}/${maxRetries}):`,
          error,
        );

        if (retries >= maxRetries) {
          // After all retries failed, notify about the error
          this.notify(SpeakerEvent.ERROR, {
            error: 'Failed to save speaker mappings after multiple attempts',
          });
          return false;
        }

        // Exponential backoff
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * Math.pow(2, retries)),
        );
      }
    }

    return false;
  }

  /**
   * Register an event listener and return a subscription
   *
   * @param event Event to listen for
   * @param callback Callback to invoke when event occurs
   * @returns An EventSubscription object for easy unsubscribing
   */
  override on(event: SpeakerEvent, callback: (data: any) => void): EventSubscription {
    return super.on(event, callback);
  }

  /**
   * Cleans up the speaker service
   */
  override cleanup(): void {
    this._speakers.clear();
    this._speakerMappings = null;
    this._speakerColorCache.clear();
    this._ensureSpeakerReferencesCache.clear();
    
    // Call base class cleanup to clear listeners
    super.cleanup();
  }

  /**
   * Ensures a Dato speaker is properly referenced in both story and album with retry logic
   *
   * @param datoSpeakerId The Dato CMS speaker ID
   * @param maxRetries Maximum number of retry attempts
   * @returns Promise resolving to true if operation was successful
   */
  async ensureSpeakerReferencesWithRetry(
    datoSpeakerId: string,
    maxRetries = 3,
  ): Promise<boolean> {
    // Generate a cache key based on the current state and params
    const cacheKey = `${datoSpeakerId}-${this._storyId}-${this._albumId}`;
    
    // Return the cached promise if it exists
    if (this._ensureSpeakerReferencesCache.has(cacheKey)) {
      return this._ensureSpeakerReferencesCache.get(cacheKey)!;
    }
    
    // If repository isn't initialized, fail early
    if (!this._speakerRepository) {
      console.error('Speaker repository not initialized');
      return false;
    }

    // If neither ID is available, fail early
    if (!this._storyId && !this._albumId) {
      console.error('Neither story ID nor album ID is available');
      return false;
    }

    // Create the promise that will do the actual work
    const operationPromise = (async (): Promise<boolean> => {
      let retries = 0;

      while (retries < maxRetries) {
        try {
          // Add to story if available
          if (this._storyId && this._speakerRepository) {
            await this._speakerRepository.addSpeakerReferenceToStory(
              datoSpeakerId,
              this._storyId,
            );
          }

          // Add to album if available
          if (this._albumId && this._speakerRepository) {
            await this._speakerRepository.addSpeakerReferenceToAlbum(
              datoSpeakerId,
              this._albumId,
            );
          }

          // If we get here, all operations succeeded
          return true;
        } catch (error) {
          retries++;
          console.warn(
            `Error ensuring speaker references (attempt ${retries}/${maxRetries}):`,
            error,
          );

          if (retries >= maxRetries) {
            this.notify(SpeakerEvent.ERROR, {
              error:
                'Failed to update speaker references after multiple attempts',
            });
            return false;
          }

          // Exponential backoff: wait longer after each failure
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * Math.pow(2, retries)),
          );
        }
      }

      return false;
    })();

    // Store the promise in the cache
    this._ensureSpeakerReferencesCache.set(cacheKey, operationPromise);
    
    // Return the promise
    return operationPromise;
  }

  /**
   * Legacy method for backwards compatibility
   * @param datoSpeakerId The Dato CMS speaker ID
   */
  private ensureSpeakerReferencesAsync(datoSpeakerId: string): void {
    // Call the new method with retry logic
    this.ensureSpeakerReferencesWithRetry(datoSpeakerId).catch((error) => {
      console.error('Error ensuring speaker references:', error);
    });
  }

  /**
   * Gets all speakers from transcript, DatoCMS, etc. and organizes them by category
   * @param container The transcript container with segments
   * @param editorStore The editor store with story and album speakers
   * @returns Object with speakers categorized as mapped, unmapped, story, and album
   */
  getAllSpeakers(container: any, editorStore: any) {
    // Get all speakers from segments
    const speakerLabels = new Set<string>();
    
    if (container) {
      container.segments.forEach((segment: any) => {
        if (segment.speakerLabel) {
          speakerLabels.add(segment.speakerLabel);
        }
      });
    }
    
    // Get a map of speakerLabel to display name
    const speakerDisplayNames: Record<string, string> = {};
    
    // Add names for transcript speakers
    Array.from(speakerLabels).forEach(speakerLabel => {
      speakerDisplayNames[speakerLabel] = this.getSpeakerName(speakerLabel) || `Speaker ${speakerLabel.replace('spk_', '')}`;
    });
    return {
      // Mapped speakers (transcript speakers with DatoCMS mappings)
      mapped: Array.from(speakerLabels)
        .filter(speakerLabel => this._speakerMappings?.mappings?.[speakerLabel])
        .map(speakerLabel => ({
          speakerLabel,
          datoSpeakerId: this._speakerMappings?.mappings?.[speakerLabel] || '',
          name: speakerDisplayNames[speakerLabel],
          title: this.getSpeakerTitle(speakerLabel),
          voice: this.getSpeakerVoice(speakerLabel),
          bio: this.getSpeakerBio(speakerLabel),
          color: this.getSpeakerColor(speakerLabel)
        }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name)),
      
      // Unmapped transcript speakers
      unmapped: Array.from(speakerLabels)
        .filter(speakerLabel => !this._speakerMappings?.mappings?.[speakerLabel])
        .map(speakerLabel => ({
          speakerLabel,
          datoSpeakerId: '',
          name: speakerDisplayNames[speakerLabel],
          title: '',
          voice: '',
          bio: '',
          color: this.getSpeakerColor(speakerLabel)
        }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name)),
      
      // Dato speakers not used in transcript
      story: editorStore.storySpeakers
        .filter((speaker: any) => 
          !Array.from(speakerLabels).some(speakerLabel => 
            this._speakerMappings?.mappings?.[speakerLabel] === speaker.id
          )
        )
        .map((speaker: any) => ({
          speakerLabel: '',
          datoSpeakerId: speaker.id,
          name: speaker.name,
          title: speaker.title,
          voice: speaker.voice,
          bio: speaker.bio,
          color: this.getSpeakerColor(speaker.id)
        }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name)),
      
      // Album speakers not used in transcript or story
      album: editorStore.albumSpeakers
        .filter((speaker: any) => 
          !editorStore.storySpeakers.some((s: any) => s.id === speaker.id) &&
          !Array.from(speakerLabels).some(speakerLabel => 
            this._speakerMappings?.mappings?.[speakerLabel] === speaker.id
          )
        )
        .map((speaker: any) => ({
          speakerLabel: '',
          datoSpeakerId: speaker.id,
          name: speaker.name,
          title: speaker.title,
          voice: speaker.voice,
          bio: speaker.bio,
          color: this.getSpeakerColor(speaker.id)
        }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name))
    };
  }

  // Public getters
  get isLoading(): boolean {
    return this._isLoading;
  }

  get storyId(): string | null {
    return this._storyId;
  }

  get albumId(): string | null {
    return this._albumId;
  }
  
  get speakerMappings(): TranscriptVoiceMapping | null {
    return this._speakerMappings;
  }
}